import { Decimal } from 'decimal.js'
import _, { isNil } from 'lodash'
import {
  ForecastComponentConfig,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPipeSection,
  GeckoTMinMethod,
  PipeWrapRegion,
} from 'types'

import { CalculateBinValueFunction } from './calculateDerivedValuesHelpers'

// - - - - - - - - - - - - - - - - - - - - - - - - Tmin by API 570 - - - - - - - - - - - - - - - - - - - - - - - -
export function getStructuralTmin(pipeOuterDiameter: number): number | null {
  // Sorts and finds designOD >= pipeOuterDiameter
  const sortedIndex = _.sortedIndex(
    API_570_STRUCTURAL_MIN_THICKNESS_MAPPING.map((entry) => entry.designOd),
    pipeOuterDiameter
  )

  // Retrieve tuple unless pipeOD exceeds maximum designOD
  const structuralTminTuple =
    sortedIndex >= 0 &&
    sortedIndex < API_570_STRUCTURAL_MIN_THICKNESS_MAPPING.length
      ? API_570_STRUCTURAL_MIN_THICKNESS_MAPPING[sortedIndex]
      : null

  return structuralTminTuple ? structuralTminTuple.minStructuralThickness : null
}

type TminByApi570Props = {
  allowable_unit_stress: GeckoMeasurement
  operation_pressure: GeckoMeasurement
  pipe_outer_diameter: GeckoMeasurement
  //  - Optional, Regions where the pipe is wrapped in protective material to increase stress tolerance
  joint_efficiency: GeckoMeasurement
  wrapRegions?: PipeWrapRegion[]
}

export function getTminByApi570BinValueFunction(
  props: TminByApi570Props
): CalculateBinValueFunction {
  const {
    allowable_unit_stress: allowableStress,
    operation_pressure: operationPressure,
    pipe_outer_diameter: pipeOuterDiameter,
    joint_efficiency: jointEfficiency,
    wrapRegions,
  } = props

  const operationPressurePsi = operationPressure.convertTo(
    GeckoMeasurementUnit.PoundsForcePerSqInch
  ).num
  const allowableStressPsi = allowableStress.convertTo(
    GeckoMeasurementUnit.PoundsForcePerSqInch
  ).num
  const pipeOdFt = pipeOuterDiameter.convertTo(GeckoMeasurementUnit.Feet).num
  const jointEfficiencyNum = jointEfficiency.num

  // Note: TMin by 574 is currently independent from the coordinate
  // TODO: when pipe OD becomes a bin property, we need to change this
  const structuralTmin = getStructuralTmin(pipeOdFt)

  return function calculateBinTminByApi570(
    activeBinRect: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    let currentAllowableStress = allowableStressPsi
    if (wrapRegions && wrapRegions.length > 0) {
      const yPosition = activeBinRect.y.start.plus(
        activeBinRect.y.delta.scale(0.5)
      )

      // We find last here assuming wrapRegions are sorted chronologically. We take the latest applied region as the true
      const currentWrapRegion = wrapRegions.findLast((region) => {
        const yMeasurementUnit = GeckoMeasurementUnit.GetByID(
          region.measurementUnit
        )
        if (!yMeasurementUnit) {
          return false
        }
        const currY = yPosition.convertTo(yMeasurementUnit).num

        return currY >= region.regionStart && currY <= region.regionEnd
      })

      if (currentWrapRegion) {
        currentAllowableStress =
          allowableStress.num + currentWrapRegion.addedStressTolerance
      }
    }

    const tmin = new Decimal(operationPressurePsi)
      .mul(pipeOdFt)
      .div(2.0)
      .div(currentAllowableStress)
      .div(jointEfficiencyNum)
      .toNumber()

    return GeckoMeasurement.CreateFromValue(
      structuralTmin ? Math.max(tmin, structuralTmin) : tmin,
      GeckoMeasurementUnit.Inches
    ).convertTo(GeckoMeasurementUnit.Millimeters)
  }
}

// ------------------------------------ Maximum Allowable Working Pressure ------------------------------------

export function getMawpBinValueFunction(
  forecastComponentConfig: ForecastComponentConfig,
  componentModel: GeckoPipeSection,
  tminMethod: GeckoTMinMethod
): CalculateBinValueFunction | undefined {
  const {
    allowable_unit_stress: allowableStress,
    pipe_outer_diameter: pipeOuterDiameter,
  } = forecastComponentConfig
  const jointEfficiency = componentModel.jointEfficiency

  if (tminMethod !== GeckoTMinMethod.TMinByApi570) {
    return undefined
  }
  if (
    isNil(allowableStress) ||
    isNil(pipeOuterDiameter) ||
    isNil(jointEfficiency)
  ) {
    throw new Error(
      `Missing configuration parameter, cannot calculate tmin by API 570: ${JSON.stringify(forecastComponentConfig)}`
    )
  }

  if (pipeOuterDiameter <= 0) {
    throw new Error(
      `Invalid pipe outer diameter, cannot calculate tmin by API 570: ${JSON.stringify(forecastComponentConfig)}`
    )
  }

  return function calculateBinMAWP(
    coordinates: GeckoComponentRect,
    thickness: GeckoMeasurement
  ) {
    return GeckoMeasurement.CreateFromValue(
      new Decimal(2)
        .times(allowableStress)
        .times(jointEfficiency)
        .times(thickness.convertTo(GeckoMeasurementUnit.Inches).num)
        .div(pipeOuterDiameter)
        .toNumber(),
      GeckoMeasurementUnit.PoundsForcePerSqInch
    )
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - Tmin by API 579 - - - - - - - - - - - - - - - - - - - - - - - -
function getPipeInnerRadius(
  pipeInnerDiameter?: number,
  pipeOuterDiameter?: number,
  pipeNominalthickness?: number
): number {
  if (!isNil(pipeInnerDiameter)) {
    return pipeInnerDiameter / 2
  }

  if (isNil(pipeOuterDiameter) || isNil(pipeNominalthickness)) {
    throw new Error(`Missing pipe inner diameter calculation params`)
  }

  return pipeOuterDiameter / 2 - pipeNominalthickness
}

export function getTminByApi579BinValueFunction(
  forecastComponentConfig: ForecastComponentConfig,
  componentModel: GeckoPipeSection,
  nominal: GeckoMeasurement | undefined
): CalculateBinValueFunction {
  const {
    allowable_unit_stress: allowableStress,
    operation_pressure: operationPressure,
    pipe_outer_diameter: pipeOuterDiameter,
    pipe_inner_diameter: pipeInnerDiameter,
  } = forecastComponentConfig

  const jointEfficiency = componentModel.jointEfficiency

  if (
    isNil(allowableStress) ||
    isNil(operationPressure) ||
    isNil(jointEfficiency)
  ) {
    throw new Error(
      `Missing configuration parameter, cannot calculate tmin by API 579: ${JSON.stringify(forecastComponentConfig)}`
    )
  }

  // Temporary, simplified implementation for pipe nominal
  // Potentially needs to be fixed in ENT-1324
  const pipeInnerRadius = getPipeInnerRadius(
    pipeInnerDiameter,
    pipeOuterDiameter,
    nominal?.convertTo(GeckoMeasurementUnit.Inches).num
  )

  return function calculateBinTminByApi579(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    coordinates: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    const numerator = new Decimal(operationPressure).mul(pipeInnerRadius)
    const denomPartial = new Decimal(0.6).mul(operationPressure)
    const denominator = new Decimal(allowableStress)
      .mul(jointEfficiency)
      .sub(denomPartial)

    const tmin = numerator.div(denominator).toNumber()

    return GeckoMeasurement.CreateFromValue(
      tmin,
      GeckoMeasurementUnit.Inches
    ).convertTo(GeckoMeasurementUnit.Millimeters)
  }
}

// ------------------------------------------------------------------------
// Sorted Default Minimum Structural Thickness for Temperatures < 400F (205C) in.
// Note: NPS converted to OD inches
export const API_570_STRUCTURAL_MIN_THICKNESS_MAPPING = _.sortBy(
  [
    { designOd: 24.0, minStructuralThickness: 0.12 }, // NPS 24
    { designOd: 18.0, minStructuralThickness: 0.11 }, // NPS 18
    { designOd: 4.5, minStructuralThickness: 0.09 }, // NPS 4
    { designOd: 3.5, minStructuralThickness: 0.08 }, // NPS 3
    { designOd: 2.375, minStructuralThickness: 0.07 }, // NPS 1/2 - NPS 2
  ],
  'designOd'
)
